import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { queryClient } from "@/lib/data";
import { applyEmbedDocumentFlags } from "@/lib/embed";
import { getRouter } from "@/router";

import "@/styles.css";

// The dealer-site embed loads straight into /sign_up inside an iframe. Flag the
// document before the first paint so the standalone app chrome never flashes
// inside the dealer's page.
applyEmbedDocumentFlags();

// Recover from stale deploys: when a dynamically imported chunk 404s because a
// newer build replaced it, reload once to pick up the fresh index.html. The
// sessionStorage guard prevents a reload loop if the failure is not deploy-related.
//
// Do NOT call event.preventDefault() here. Preventing the default makes Vite's
// preload helper swallow the error and resolve the failed import() to `undefined`
// instead of rejecting. Downstream `const { X } = await import(...)` then throws a
// misleading "Cannot destructure property 'X' from null or undefined value" before
// the reload tears down the page. Letting it reject surfaces the real
// "failed to fetch dynamically imported module" error and reloads cleanly.
window.addEventListener("vite:preloadError", () => {
  const RELOAD_KEY = "vite-preload-reloaded";
  if (sessionStorage.getItem(RELOAD_KEY)) return;
  sessionStorage.setItem(RELOAD_KEY, "1");
  window.location.reload();
});

if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
  });
}

const router = getRouter();

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      // eslint-disable-next-line @typescript-eslint/unbound-method -- Sentry passes a bound function
      fallback={({ resetError }) => (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <div className="max-w-sm text-center">
            <h1 className="font-heading text-2xl font-semibold text-foreground">
              Something went wrong
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              An unexpected error occurred. Please try again.
            </p>
            <button
              type="button"
              onClick={resetError}
              className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            >
              Try again
            </button>
          </div>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);

// The app booted successfully, so clear the reload guard. This lets a later
// deploy in the same session recover via a fresh reload if it too goes stale.
window.addEventListener("load", () => {
  sessionStorage.removeItem("vite-preload-reloaded");
});
